/**
 * Zone de dépôt d'un fichier : on le GLISSE dessus, ou on clique pour le choisir.
 *
 * Règle permanente (Benjamin, 2026-10-12) : partout où l'on peut importer ou joindre un document, on
 * peut aussi le déposer d'un glisser. Tout écran qui reçoit un fichier passe donc par ce composant, pas
 * par un `<input type="file">` nu. Le tri de ce qui est déposé (nombre, format) vit dans
 * `domaine/depot.ts`, où il se teste ; le contrôle réel du fichier reste côté serveur.
 *
 * Un fichier lâché à côté d'une zone ferait ouvrir le fichier par le navigateur, en quittant l'écran :
 * la zone intercepte donc tout glissé de fichiers qui la survole, même désactivée (elle l'ignore alors).
 */
import { useState, type DragEvent } from 'react';

import { porteDesFichiers, retenirFichiers } from '../domaine/depot';

export function ZoneDepot({
  onFichiers,
  accept,
  multiple = false,
  desactive = false,
  compacte = false,
  libelle,
  nomChoisi,
  boutonLibelle,
  ariaLabel,
}: {
  /** Les fichiers retenus (jamais plus d'un quand `multiple` est faux, jamais d'un format refusé). */
  onFichiers: (fichiers: readonly File[]) => void;
  /** Formats acceptés, comme l'attribut `accept` d'un champ fichier (`.xlsx`, `application/pdf`). */
  accept?: string;
  multiple?: boolean;
  desactive?: boolean;
  /** Variante basse, pour une zone au milieu d'une liste. */
  compacte?: boolean;
  /** Texte à gauche du bouton. Par défaut : « Glissez le fichier ici, ou ». */
  libelle?: string;
  /** Le fichier déjà choisi, rappelé sous le texte. */
  nomChoisi?: string | null;
  boutonLibelle?: string;
  ariaLabel?: string;
}) {
  const [survol, setSurvol] = useState(false);
  const [refus, setRefus] = useState<string | null>(null);

  const surSurvol = (e: DragEvent<HTMLDivElement>) => {
    if (!porteDesFichiers(Array.from(e.dataTransfer.types))) return;
    // Sans ce `preventDefault`, le navigateur ouvre le fichier lâché au lieu de nous le donner.
    e.preventDefault();
    if (!desactive && !survol) setSurvol(true);
  };

  const surSortie = (e: DragEvent<HTMLDivElement>) => {
    // `dragleave` se déclenche aussi en passant d'un enfant à un autre : ne retirer la mise en
    // évidence qu'en quittant réellement la zone.
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setSurvol(false);
  };

  const traiter = (deposes: readonly File[]) => {
    const retenus = retenirFichiers(deposes, { multiple, ...(accept === undefined ? {} : { accept }) });
    setRefus(retenus.refus);
    if (retenus.fichiers.length > 0) onFichiers(retenus.fichiers);
  };

  const surDepot = (e: DragEvent<HTMLDivElement>) => {
    if (!porteDesFichiers(Array.from(e.dataTransfer.types))) return;
    e.preventDefault();
    setSurvol(false);
    if (desactive) return;
    traiter(Array.from(e.dataTransfer.files));
  };

  const classes = [
    'zone-depot-fichier',
    compacte ? 'compacte' : '',
    survol ? 'survol' : '',
    desactive ? 'inactive' : '',
  ]
    .filter((c) => c !== '')
    .join(' ');

  return (
    <div className={classes} onDragOver={surSurvol} onDragEnter={surSurvol} onDragLeave={surSortie} onDrop={surDepot}>
      <span className="zone-depot-texte">
        {survol ? 'Déposez le fichier ici' : (libelle ?? (multiple ? 'Glissez vos fichiers ici, ou' : 'Glissez le fichier ici, ou'))}
        {nomChoisi ? <span className="zone-depot-nom">{nomChoisi}</span> : null}
      </span>
      <label className={`bouton-lien discret televerser${desactive ? ' desactive' : ''}`}>
        {boutonLibelle ?? (multiple ? 'Choisir des fichiers' : 'Choisir un fichier')}
        <input
          type="file"
          hidden
          disabled={desactive}
          multiple={multiple}
          {...(accept === undefined ? {} : { accept })}
          {...(ariaLabel === undefined ? {} : { 'aria-label': ariaLabel })}
          onChange={(e) => {
            const choisis = Array.from(e.target.files ?? []);
            // Remis à zéro : choisir de nouveau le MÊME fichier (après l'avoir corrigé dans Excel) doit
            // déclencher le changement.
            e.target.value = '';
            if (choisis.length > 0) traiter(choisis);
          }}
        />
      </label>
      {refus ? <p className="message erreur zone-depot-refus">{refus}</p> : null}
    </div>
  );
}
